// Helpers for event dates, including optional multi-day events.
// A multi-day event has an `endDate` field in addition to `date`; it stays
// visible in listings until midnight at the end of its last day and its
// date is displayed as a range (e.g. "11–13 september").

export function toJsDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const d = new Date(value);
  return isNaN(d) ? null : d;
}

// The date an event stops being "upcoming": endDate for multi-day events,
// otherwise the (start) date.
export function getEventEnd(event) {
  return toJsDate(event.endDate) || toJsDate(event.date);
}

export function isOngoingOrUpcoming(event, startOfToday) {
  const end = getEventEnd(event);
  return !!end && end >= startOfToday;
}

// Firestore can't express "date >= today OR endDate >= today", so upcoming
// queries widen their window by this many days and filter client-side with
// isOngoingOrUpcoming. Multi-day events longer than this may disappear from
// listings before they end.
export const MULTI_DAY_LOOKBACK_DAYS = 14;

export function upcomingQueryStart(startOfToday) {
  const d = new Date(startOfToday);
  d.setDate(d.getDate() - MULTI_DAY_LOOKBACK_DAYS);
  return d;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// For multi-day events, returns the pieces of a two-line display
// ("31 december 2026, 16:52 –" / "2 januari 2027, 16:52") so narrow layouts
// like the event panels don't overflow. Returns null for single-day events.
export function getMultiDayParts(startValue, endValue, locale) {
  const start = toJsDate(startValue);
  const end = toJsDate(endValue);
  if (!start || !end || end <= start || sameDay(start, end)) return null;
  const dateOpts = { day: 'numeric', month: 'long' };
  // Include years when the range crosses a year boundary
  if (start.getFullYear() !== end.getFullYear()) dateOpts.year = 'numeric';
  const timeOpts = { hour: '2-digit', minute: '2-digit' };
  return {
    startDate: start.toLocaleDateString(locale, dateOpts),
    startTime: start.toLocaleTimeString(locale, timeOpts),
    endDate: end.toLocaleDateString(locale, dateOpts),
    endTime: end.toLocaleTimeString(locale, timeOpts)
  };
}

// Display fields for the event panels (home page and /events). Single-day
// events get the classic uppercase weekday date + start time; multi-day
// events get start/end parts that EventDateTime renders over two lines.
export function getPanelDateFields(dateValue, endValue, locale) {
  const start = toJsDate(dateValue) || new Date();
  const startTime = start.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const parts = getMultiDayParts(dateValue, endValue, locale);
  if (parts) {
    return {
      multiDay: true,
      formattedDate: parts.startDate.toUpperCase(),
      formattedTime: parts.startTime,
      formattedEndDate: parts.endDate.toUpperCase(),
      formattedEndTime: parts.endTime
    };
  }
  return {
    multiDay: false,
    formattedDate: start.toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    }).toUpperCase(),
    formattedTime: startTime,
    formattedEndDate: null,
    formattedEndTime: null
  };
}

// Formats a single date with `options`, or a locale-aware range
// ("11–13 september" / "30 september – 2 oktober") when endValue is a
// different day. Weekday is dropped for ranges to keep them readable.
export function formatEventDateRange(startValue, endValue, locale, options) {
  const start = toJsDate(startValue);
  if (!start) return '';
  const end = toJsDate(endValue);
  if (!end || end <= start || sameDay(start, end)) {
    return start.toLocaleDateString(locale, options);
  }
  const { weekday, ...rangeOptions } = options || {};
  try {
    return new Intl.DateTimeFormat(locale, rangeOptions).formatRange(start, end);
  } catch (e) {
    return `${start.toLocaleDateString(locale, rangeOptions)} – ${end.toLocaleDateString(locale, rangeOptions)}`;
  }
}
